import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, ArrowRight, CircleDot } from 'lucide-react'
import { useGlobalTimeline, useGlobalOpenActions } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { formatDateOnly, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'
import { CategoryFilterPills, TimelineList } from '@/components/timeline'
import { roleLabel } from '@/features/purchase-orders/action-card-model'

function formatRelativeDate(dateStr: string) {
  const diff = Date.now() - parseUTCDate(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function ProjectsTimelinePage() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const { opsPersonId, staffId } = useMyProjectsFilter()
  const { data: events, isLoading: timelineLoading } = useGlobalTimeline({
    event_category: activeCategory || undefined,
    ops_person_id: opsPersonId,
    limit: 200,
  })
  const { data: actions, isLoading: actionsLoading } = useGlobalOpenActions({
    ops_person_id: opsPersonId,
    staff_id: staffId,
  })
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Timeline</h1>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Left column: Timeline — shown second on mobile so Open Actions come first */}
        <div className="order-2 min-w-0 space-y-4 lg:order-1">
          {timelineLoading ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-24 rounded-full" />
                ))}
              </div>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : events?.length ? (
            <>
              <CategoryFilterPills
                events={events}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
              <TimelineList events={events} showProject />
            </>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-8">
                <CircleDot className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No events across active projects</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Open Actions — shown first on mobile */}
        <div className="order-1 lg:order-2">
          <Card>
            <CardHeader className="flex flex-row items-center gap-2 pb-3">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Open Actions</CardTitle>
              {(actions?.length ?? 0) > 0 && (
                <Badge variant="destructive" className="ml-auto">
                  {actions!.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {actionsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : actions?.length ? (
                <div className="space-y-3">
                  {actions.map((action) => (
                    <div
                      key={action.id}
                      className="rounded-lg border p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium leading-snug">
                            {action.summary}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                            <Link
                              to="/purchase-orders/$poId"
                              params={{ poId: action.po_uuid }}
                              className="font-mono font-medium text-primary hover:underline"
                            >
                              {action.po_number}
                            </Link>
                            {action.manufacturer_name && (
                              <>
                                <span>&middot;</span>
                                {action.manufacturer_uuid ? (
                                  <Link
                                    to="/manufacturers/$manuId"
                                    params={{ manuId: action.manufacturer_uuid }}
                                    className="text-primary hover:underline"
                                  >
                                    {action.manufacturer_name}
                                  </Link>
                                ) : (
                                  <span>{action.manufacturer_name}</span>
                                )}
                              </>
                            )}
                          </div>
                          {action.project_name && action.project_uuid && (
                            <Link
                              to="/projects/$projectId"
                              params={{ projectId: action.project_uuid }}
                              className="mt-0.5 block text-xs text-primary hover:underline"
                            >
                              {action.project_name}
                            </Link>
                          )}
                        </div>
                        <span
                          className="shrink-0 text-[10px] text-muted-foreground"
                          title={`Created ${parseUTCDate(action.created_at).toLocaleString()}`}
                        >
                          {formatRelativeDate(action.created_at)}
                        </span>
                      </div>

                      {action.details && (
                        <p className="text-xs text-muted-foreground">{action.details}</p>
                      )}

                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          {formatStatus(action.action_type)}
                        </Badge>
                        {action.assigned_role && (
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                            action.assigned_role === 'sales'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-indigo-100 text-indigo-800'
                          }`}>
                            {roleLabel(action.assigned_role)}
                          </span>
                        )}
                        {action.charge_amount != null && Number(action.charge_amount) > 0 && (
                          <span className="text-xs font-medium">
                            {formatMoney(action.charge_amount)}
                          </span>
                        )}
                        {action.expected_date && (
                          <span className="text-[10px] text-muted-foreground">
                            Due {formatDateOnly(action.expected_date)}
                          </span>
                        )}
                        <Link
                          to="/purchase-orders/$poId"
                          params={{ poId: action.po_uuid }}
                          search={{ tab: 'actions' }}
                          className="ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium text-primary hover:bg-accent"
                        >
                          View
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No open actions
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
